import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Button from '../components/common/Button';
import './MarketingHome.css';

const MarketingHome = () => {
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    '100 athletes per team',
    'Workout calendar and scheduling',
    'Athlete progress tracking',
    'One-rep max calculations',
    'Exercise library with videos',
    'Team access codes',
    'Multiple team support',
    'Stats and leaderboards'
  ];

  return (
    <div className="marketing-page">
      {/* Sticky Navigation */}
      <nav className="marketing-nav">
        <div className="nav-container">
          <div className="nav-links">
            <button onClick={() => scrollToSection('hero')} className="nav-link">Home</button>
            <button onClick={() => scrollToSection('how-it-works')} className="nav-link">How It Works</button>
            <button onClick={() => scrollToSection('pricing')} className="nav-link">Pricing</button>
            <button onClick={() => scrollToSection('about')} className="nav-link">About</button>
          </div>
          <span className="nav-logo">Three Plates</span>
          <div className="nav-buttons">
            <Button variant="ghost" onClick={() => navigate('/login')}>
              Login
            </Button>
            <Button variant="primary" onClick={() => navigate('/signup')}>
              Sign Up
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-image-placeholder">
              <img src="/team-photo.png" alt="ThreePlates" className="hero-image" />
            </div>
            <h1 className="hero-headline">The simple strength programming app.</h1>
            <p className="hero-subheadline">TeamBuildr and Volt are packed with dashboards, reports, and features you don’t touch. We cut the friction so you can do what you do best: coach. Open the app, see your workouts, and get to work.</p>
            <Button variant="primary" size="lg" onClick={() => navigate('/signup')}>
              Get Started
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="container">
          <h2 className="section-title">How It Works</h2>
          <div className="how-it-works-content">
            <div className="video-container">
              <iframe
                width="315"
                height="560"
                src="https://www.youtube.com/embed/MxUH7OdproE"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Three Plates Demo"
              ></iframe>
            </div>
            <div className="how-it-works-steps">
              <div className="hiw-step">
                <div className="hiw-step-number">1</div>
                <h3>Create Your Program</h3>
                <p>Build workouts with our intuitive calendar. Set exercises, sets, reps, and percentages.</p>
              </div>
              <div className="hiw-step">
                <div className="hiw-step-number">2</div>
                <h3>Athletes Join Your Team</h3>
                <p>Share your team code. Athletes download the app and instantly see their workouts.</p>
              </div>
              <div className="hiw-step">
                <div className="hiw-step-number">3</div>
                <h3>Track Progress</h3>
                <p>Athletes log their lifts. You see real-time stats, PRs, and workout completion.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="container">
          <h2 className="section-title">Pricing</h2>
          <p className="section-subtitle">Simple pricing for teams of any size</p>
          <div className="pricing-single">
            <div className="pricing-card highlighted">
              <h3 className="pricing-tier-name">Team Program</h3>
              <div className="pricing-price">
                <span className="price">$99.99</span>
                <span className="period">/month</span>
              </div>
              <p className="pricing-tagline">Everything you need to run your program</p>
              <ul className="pricing-features">
                {features.map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>
              <Button
                variant="primary"
                block
                onClick={() => navigate('/signup')}
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <h2 className="section-title">About Three Plates</h2>
          <p className="about-text">
            Three Plates was built by coaches, for coaches. We were tired of spreadsheets, group chats, and guesswork.
            Our platform gives strength coaches everything they need to program workouts, track athlete progress, and
            build stronger teams. No more lost workout sheets. No more manual calculations. Just results.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="marketing-footer">
        <div className="container">
          <div className="footer-links">
            <Link to="/about">About</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/privacy">Privacy</Link>
          </div>
          <p className="copyright">&copy; 2024 Three Plates. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default MarketingHome;
